import React, { useCallback, useEffect, useState } from "react";

import { isEmpty } from "ramda";
import { useParams } from "react-router-dom";

import referenceSourceValuesApi from "apis/reference_source_values";
import referenceSourceApi from "apis/reference_sources";
import { Button, PageLoader, Typography } from "clearsense-ui/components";
import { Plus } from "components/Common/clearsense-ui/icons";
import ExploreContainer from "components/Common/ExploreContainer";
import FilterHeader from "components/Common/FilterHeader";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import { useUserState } from "contexts/user";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";
import { getPermissions } from "utils/roleBasedAccess";

import CreateValues from "./CreateValues";
import ValuesTable from "./Table";

import { VALUE_COLUMNS } from "../../constants/valueReferenceSource";
import DeleteValues from "../DeleteAlert";
import SummaryCard from "../SummaryCard";

const Values = ({ fetchReferenceSourceData, features }) => {
  const { referenceSourceId } = useParams();

  const [isCreateValuesOpen, setIsCreateValuesOpen] = useState(false);
  const [checkedRows, setCheckedRows] = useState([]);
  const [rowToBeDeleted, setRowToBeDeleted] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [rowToBeEdited, setRowToBeEdited] = useState({});
  const [referenceSource, setReferenceSource] = useState({});
  const [referenceSourceValues, setReferenceSourceValues] = useState({
    count: 0,
    values: [],
  });

  const user = useUserState();
  const userPermissions = getPermissions(features, user?.role);

  const fetchReferenceSource = async () => {
    try {
      const { data } = await referenceSourceApi.show(referenceSourceId);
      setReferenceSource(data);
    } catch (error) {
      logger.log(error);
    }
  };

  useEffect(() => {
    fetchReferenceSource();
  }, []);

  const fetchReferenceSourceValuesData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const { data } = await referenceSourceValuesApi.index(
        referenceSourceId,
        search.toLowerCase(),
        currentPage,
        pageSize
      );
      setReferenceSourceValues({
        count: data?.reference_source_value_count,
        values: data?.reference_source_values,
      });
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteRecords = async () => {
    try {
      setIsLoading(true);
      const checkedRowIds = isEmpty(rowToBeDeleted)
        ? checkedRows.map(checkedRow => checkedRow.id)
        : [rowToBeDeleted.id];
      await referenceSourceValuesApi.destroy_by_ids({
        ids: checkedRowIds,
      });
      fetchReferenceSourceValuesData();
      fetchReferenceSourceData();
      setCheckedRows([]);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = record => {
    setRowToBeDeleted(record);
    setIsDeleteModalOpen(true);
  };

  const handleEdit = record => {
    setIsEdit(true);
    setRowToBeEdited(record);
    setIsCreateValuesOpen(true);
  };

  const filterParams = useSearchAndPaginate(fetchReferenceSourceValuesData, {
    page: () => setCheckedRows([]),
    pageSize: () => setCheckedRows([]),
  });

  const columnHeaderSelector = useColumnHeaderSelector(
    VALUE_COLUMNS(handleEdit, handleDelete, userPermissions)
  );

  return (
    <>
      <ExploreContainer>
        <div className="flex justify-between">
          <Typography style="h3" type="semibold">
            {`Values in ${referenceSource.name} (${referenceSourceValues.count})`}
          </Typography>
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="create"
          >
            <Button
              label="Create Value"
              icon={Plus}
              data-cy="values-create-value"
              onClick={() => {
                setIsCreateValuesOpen(true);
              }}
              data-testid="create-button"
            />
          </PermissibleWrapper>
        </div>
        <SummaryCard referenceSource={referenceSource} />
        <FilterHeader
          entity="Value"
          columnHeaderSelector={columnHeaderSelector}
          count={referenceSourceValues?.count}
          filterParams={filterParams}
          checkedRows={checkedRows}
          setIsDeleteAlertOpen={setIsDeleteModalOpen}
          userPermissions={userPermissions}
        />
        {isLoading ? (
          <PageLoader />
        ) : (
          <ValuesTable
            referenceSourceValues={referenceSourceValues}
            userPermissions={userPermissions}
            columns={columnHeaderSelector.columns}
            setCheckedRows={setCheckedRows}
            checkedRows={checkedRows}
            filterParams={filterParams}
            setIsPaneOpen={setIsCreateValuesOpen}
          />
        )}
      </ExploreContainer>
      <CreateValues
        isCreateValuesOpen={isCreateValuesOpen}
        setIsCreateValuesOpen={setIsCreateValuesOpen}
        fetchReferenceSourceValuesData={fetchReferenceSourceValuesData}
        isEdit={isEdit}
        setIsEdit={setIsEdit}
        rowToBeEdited={rowToBeEdited}
        setRowToBeEdited={setRowToBeEdited}
        fetchReferenceSourceData={fetchReferenceSourceData}
      />
      <DeleteValues
        entityName="Value"
        deleteRecords={deleteRecords}
        checkedRows={checkedRows}
        rowToBeDeleted={rowToBeDeleted}
        setRowToBeDeleted={setRowToBeDeleted}
        isDeleteAlertOpen={isDeleteModalOpen}
        setIsDeleteAlertOpen={setIsDeleteModalOpen}
        setCheckedRows={setCheckedRows}
      />
    </>
  );
};

export default Values;
